import React from "react";
import styled from "styled-components";
import Helmet from "react-helmet";
import { H3, P, Description } from "../components/typography";
import { withState } from "recompose";
import { getGraphNode } from "../selectors";
import NavBar from "../components/NavBar";
import Pad from "../components/blocks/Pad";
import Flex, { HFlexFlipper } from "../components/blocks/Flex";
import { r } from "../utils";
import { ShowMobile } from "../components/blocks/Responsive";
import Spacer from "../components/blocks/Spacer";
import Background from "../components/blocks/Background";
import Section from "../components/blocks/Section";
import Pipeline from "../components/Pipeline";
import FadeIn from "../components/atoms/FadeIn";
import Image from "../components/Image";
import ArrowLink from "../components/ArrowLink";
import { InternalButton } from "../components/atoms/Button";
import favicon from "../../assets/logofavcon@2x.png";
import nestImage from "../../nest-instigator.jpeg";

const HeroImageWrapper = styled(Pad)`
  flex: 5;
  margin-left: 0rem;

  ${props => props.theme.media.tablet`
    margin-left: 0rem;
    padding-right: 3rem;
  `};
`;

const HFlexFlipperReverse = Flex.extend`
  ${r("flex-direction", "row-reverse", "column")};
`;

const HeroText = styled.div`
  flex: 5;
  padding-left: 6rem;

  ${props => props.theme.media.tablet`
    padding-left: 1rem;
    padding-right: 1rem;
  `};
`;

const TrialPage = ({ background, onColorChange, data }) => {
  const content = getGraphNode(data, "TrialPage");
  return (
    <div>
      <Background>
        <Helmet
          title="Pipeline"
          meta={[{ name: "Contraline", content: "Pipeline" }]}
        >
          <link rel="icon" type="image/png" href={favicon} />
        </Helmet>
        <NavBar color="dark" />

        {/* Pipeline Visualization */}
        <Section padLarge>
            <Pad v={1}>
              <P style={{ textAlign: "center", maxWidth: 650, margin: "0 auto" }}>
                {(content.pipelineIntro && content.pipelineIntro.pipelineIntro) || "Contraline is developing a portfolio of male contraceptive products to give men real choices in family planning."}
              </P>
              <Spacer span={5} />
              <FadeIn style={{ transitionDelay: "1000ms" }}>
                <Pipeline products={content.pipelineProducts && content.pipelineProducts.map(function(p) {
                  return {
                    name: p.name,
                    subtitle: p.subtitle,
                    indications: [{ disease: p.indicationDisease, progress: p.indicationProgress, label: p.indicationLabel }]
                  };
                })} />
              </FadeIn>
              <FadeIn style={{ transitionDelay: "2000ms", textAlign: "center", marginTop: "8rem", color: "#000914", fontSize: "3rem" }}>
                <div style={{ transform: "rotate(90deg)" }}>&#x203A;</div>
              </FadeIn>
            </Pad>
          </Section>

        {/* NES/T Study Section */}
        <Section v={8}>
          <HFlexFlipper>
            <HeroText style={{ paddingLeft: 0, paddingRight: "6rem" }}>
              <H3>{content.nestSectionTitle || "NES/T Hormonal Gel"}</H3>
              <P>
                {(content.nestSectionBody && content.nestSectionBody.nestSectionBody) || "Phase 2B clinical trials have been completed, demonstrating strong efficacy and tolerability. Contraline exclusively licensed NES/T from the Population Council to bring it to market."}
              </P>
              {content.nestSectionBody2 && content.nestSectionBody2.nestSectionBody2 &&
                <P>
                  {content.nestSectionBody2.nestSectionBody2}
                </P>
              }
              <Pad v={0.75}>
                <ArrowLink color={"teal"} to={"/nestorone"}>
                  <P color={"teal"} style={{ paddingBottom: 0 }}>
                    {content.nestSectionLinkText || "Learn more about NES/T"}
                  </P>
                </ArrowLink>
              </Pad>
            </HeroText>
            <HeroImageWrapper h={1} mh={0.5} style={{ paddingRight: 0 }}>
              <img
                src={nestImage}
                alt="NES/T gel"
                style={{ width: "100%", height: "auto" }}
              />
            </HeroImageWrapper>
          </HFlexFlipper>
        </Section>

        {/* ADAM Study Section (existing CMS content) */}
        <ShowMobile><div style={{ height: 16 }} /></ShowMobile>
        <Section v={8}>
          <HFlexFlipperReverse>
            <HeroText>
              <H3>
                {content.heroTitle || "ADAM Implant"}
              </H3>
              <P>
                {content.heroBody.heroBody}
              </P>
              <Spacer span={0.5} />
              <Description>
                {content.heroLink &&
                  <Pad v={0.75}>
                    <ArrowLink color={"teal"} to={"/product"}>
                      <P color={"teal"} style={{ paddingBottom: 0 }}>
                        {content.heroLink}
                      </P>
                    </ArrowLink>
                  </Pad>}
              </Description>
            </HeroText>
            <ShowMobile>
              <Spacer span={3} />
            </ShowMobile>
            <HeroImageWrapper h={1} mh={0.5} style={{ paddingLeft: 0 }}>
              <Image
                full={true}
                align="center"
                caption={""}
                image={content.heroImage.responsiveResolution.src}
              />
            </HeroImageWrapper>
          </HFlexFlipperReverse>
        </Section>

        <Spacer span={4} />
      </Background>
    </div>
  );
};

const enhance = withState("background", "onColorChange", "transparent");

export default enhance(TrialPage);

export const pageQuery = graphql`
  query TrialPageQuery {
    allContentfulTrialPage {
      edges {
        node {
          heroTitle
          heroBody {
            heroBody
          }
          heroImage {
            responsiveResolution(width: 800, quality: 70) {
              src
            }
          }
          heroLink
          pipelineIntro {
            pipelineIntro
          }
          nestSectionTitle
          nestSectionBody {
            nestSectionBody
          }
          nestSectionLinkText
          nestSectionLink
          ctaBody {
            ctaBody
          }
          ctaTitle
          ctaLink
          pipelineProducts {
            name
            subtitle
            indicationDisease
            indicationProgress
            indicationLabel
          }
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/pages/trials.js